import type { Dispatch, ReactNode } from 'react';
import {
  isOpenerVariant,
  presetsFor,
  type AnimationConfig,
  type PreviewTemplate,
} from '@game-platform/pack-animation';
import { Select } from '../../components/ui/Select';
import { ControlsPanel } from './ControlsPanel';
import { KnobsPanel } from './KnobsPanel';
import { MechanicPicker } from './MechanicPicker';
import { SoundPanel } from './SoundPanel';
import type { EditorAction } from './useAnimationEditor';

interface Props {
  animation: AnimationConfig;
  template: PreviewTemplate;
  dispatch: Dispatch<EditorAction>;
  /** The mechanic changed: the preview scene is about to remount. */
  onVariantChange: () => void;
  /** Shown above the mechanic. */
  children?: ReactNode;
}

/** The left column: mechanic, preset, sound, controls and knobs of the edited animation. */
export function AnimationEditorPanel({ animation, template, dispatch, onVariantChange, children }: Props) {
  const isOpener = isOpenerVariant(animation.variant);
  const presets = isOpenerVariant(animation.variant) ? presetsFor(animation.variant) : [];

  return (
    <aside className="w-[360px] shrink-0 overflow-y-auto border-r border-[var(--co-border)] bg-[var(--co-surface)] p-4">
      {children}
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
        Mechanic
      </h2>
      <MechanicPicker
        value={animation.variant}
        onChange={(variant) => {
          dispatch({ type: 'set-variant', variant });
          onVariantChange();
        }}
      />
      {isOpener && presets.length > 1 && (
        <div className="mt-4">
          <label className="mb-1 block text-[12px] text-[var(--co-text)]" htmlFor="animation-preset">
            Preset
          </label>
          <Select
            id="animation-preset"
            aria-label="Preset"
            value={animation.preset ?? presets[0]!}
            options={presets.map((p) => ({ value: p, label: p }))}
            onChange={(preset) => dispatch({ type: 'set-preset', preset })}
          />
        </div>
      )}
      {isOpener && (
        <div className="mt-5">
          <SoundPanel
            sound={animation.sound}
            onChange={(sound) => dispatch({ type: 'set-sound', sound })}
          />
          <div className="mt-5">
            <ControlsPanel
              variant={animation.variant}
              template={template}
              controls={animation.controls}
              onChange={(controls) => dispatch({ type: 'set-controls', controls })}
            />
          </div>
        </div>
      )}
      <div className="mt-5">
        <KnobsPanel
          variant={animation.variant}
          template={template}
          options={animation.options}
          onKnobChange={(path, value) => dispatch({ type: 'set-knob', path, value })}
        />
      </div>
    </aside>
  );
}
